// @ts-nocheck

import '../styles/globals.css';

import { COOKIES, SWR_KEYS } from '@bulknutrients/bulk-shared-lib/constants';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { UIContextProvider } from '@components/Context/UIContext/UIContext';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { DatadogPagesRouter } from '@datadog/browser-rum-nextjs';
import Intercom from '@intercom/messenger-js-sdk';
import * as Tooltip from '@radix-ui/react-tooltip';
import { SpeedInsights } from '@vercel/speed-insights/next';
import Cookies from 'js-cookie';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import localFont from 'next/font/local';
import Head from 'next/head';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { mutate } from 'swr';
import { v4 as uuid } from 'uuid';

const UnsupportedBrowserToast = dynamic(() =>
  import(
    '../components/BrowserCompatibilityAlert/BrowserCompatibilityAlert'
  ).then((mod) => mod.UnsupportedBrowserToast)
);

const ExitPreview = dynamic(() =>
  import('../components/Button/DevButtons').then((mod) => mod.ExitPreview)
);

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const Geogrotesque = localFont({
  src: [
    { path: '../public/font/Geogrotesque-Regular.otf', weight: '400' },
    {
      path: '../public/font/Geogrotesque-RegularIt.otf',
      weight: '400',
      style: 'italic',
    },
    { path: '../public/font/Geogrotesque-Bold.otf', weight: '600' },
    {
      path: '../public/font/Geogrotesque-BoldIt.otf',
      weight: '600',
      style: 'italic',
    },
    { path: '../public/font/Geogrotesque-SemiBold.otf', weight: '600' },
    {
      path: '../public/font/Geogrotesque-SemiBoldIt.otf',
      weight: '600',
      style: 'italic',
    },
  ],
  display: 'swap', // helps performance on LCP
});

const Tungsten = localFont({
  src: [
    { path: '../public/font/Tungsten-Book.otf', weight: '400' },
    {
      path: '../public/font/Tungsten-Semibold.otf',
      weight: '600',
    },
  ],
  display: 'swap', // helps performance on LCP
});

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const [removedCartItems, setRemovedCartItems] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);

  // Hydration check - runs once after mount
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Firefox scroll fix - only after hydration
  useEffect(() => {
    if (!isHydrated) return;

    if (navigator.userAgent.indexOf('Firefox') > -1) {
      document.querySelector('html').style.scrollBehavior = 'auto';
      document.querySelector('body').style.scrollBehavior = 'auto';
    }
  }, [isHydrated]);

  // Check all cart items are available - only after hydration
  useEffect(() => {
    if (!isHydrated) return;

    let mounted = true;
    fetch('/api/cart/check-stock')
      .then((res) =>
        res.json().then((response) => {
          if (response.data === true) {
            if (mounted) setRemovedCartItems(true);
            mutate(SWR_KEYS.cart);
          }
        })
      )
      .catch((err) => {
        console.error(err);
        if (mounted) setRemovedCartItems(false);
      });

    return function cleanup() {
      mounted = false;
    };
  }, [isHydrated]);

  // Session and customer cookies - only after hydration
  useEffect(() => {
    if (!isHydrated) return;

    // Check for session cookie
    const sessionCookie = Cookies.get(COOKIES.session);
    if (!sessionCookie) {
      // Create the cookie
      Cookies.set(COOKIES.session, uuid());
    }

    // Check for customer cookie
    const customerCookie = Cookies.get(COOKIES.tracking);
    if (!customerCookie) {
      // Create the cookie
      Cookies.set(COOKIES.tracking, uuid(), {
        expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
        sameSite: 'lax',
        httpOnly: false,
      });
    } else {
      // Update the cookie expiration
      Cookies.set(COOKIES.tracking, customerCookie, {
        expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
        sameSite: 'lax',
        httpOnly: false,
      });
    }
  }, [isHydrated]);

  // Intercom integration - only after hydration
  useEffect(() => {
    if (!isHydrated) return;

    const intercomjwt = Cookies.get(COOKIES.intercom);
    if (!intercomjwt) {
      Intercom({
        app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID as string,
        region: 'ap',
      });
    } else {
      // If we have an Intercom JWT, boot Intercom with it to identify the user
      Intercom({
        app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID as string,
        region: 'ap',
        intercom_user_jwt: intercomjwt,
      });
    }
  }, [isHydrated]);

  return (
    <>
      <style jsx global>{`
        :root {
          --tungsten-font: ${Tungsten.style.fontFamily};
          --geogrotesque-font: ${Geogrotesque.style.fontFamily};
        }
      `}</style>
      {/* Google Tag Manager Script */}
      <Script id="gtm-script" strategy="lazyOnload">
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          '/wa-bulknutrients';f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-5M7TVR4');
        `}
      </Script>
      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>
          Bulk Nutrients | Australia's first choice for nutritional supplements
        </title>
        <link rel="icon" href="/favicon.ico" sizes="32x32" />
        <link rel="icon" href="/icon.svg" type="image/svg+xml" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/manifest.json" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://www.schema.org',
              '@type': 'WebSite',
              name: 'Bulk Nutrients',
              url: 'https://www.bulknutrients.com.au/',
            }),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              url: 'https://www.bulknutrients.com.au/',
              potentialAction: {
                '@type': 'SearchAction',
                target:
                  'https://www.bulknutrients.com.au/search-results-page?q={search_term_string}',
                'query-input': 'required name=search_term_string',
              },
            }),
          }}
        />
      </Head>
      <ErrorAlert
        title="Out of Stock Item"
        show={removedCartItems}
        setShow={setRemovedCartItems}
      >
        <Paragraph>
          One or more items you had in your cart are currently out of stock.
          We've removed them for you so you are able to checkout.
        </Paragraph>
      </ErrorAlert>
      <UIContextProvider>
        <Tooltip.Provider>
          {pageProps.isPreview && <ExitPreview />}
          <DatadogPagesRouter />
          {getLayout(<Component {...pageProps} />)}
          <SpeedInsights sampleRate={0.2} />
          <UnsupportedBrowserToast />
        </Tooltip.Provider>
      </UIContextProvider>
    </>
  );
}

export default MyApp;
